import type { ReactElement } from 'react'
import { IconClose } from './icons/IconClose'
import styles from './CloseButton.module.css'

export type CloseButtonVariant = 'phone' | 'tablet'

export interface CloseButtonProps {
  /** Called when the user taps the close (×) button to dismiss the route panel. */
  readonly onClick?: () => void
  /** Accessible label. @defaultValue `'Close route'` */
  readonly ariaLabel?: string
  /**
   * Layout variant — drives button size, matching the adjacent
   * {@link SwapButton} exactly.
   * - `'phone'`: 48×48 (collapsed route card)
   * - `'tablet'`: 52×52
   *
   * @defaultValue `'tablet'`
   */
  readonly variant?: CloseButtonVariant
}

/**
 * Round close (×) button for the route-built panel's destinations row
 * (Figma spec §1.4). Sized to MATCH the adjacent SwapButton per variant
 * (48×48 phone / 52×52 tablet — 2026-07-06), `bg/secondary` fill,
 * byte-identical `icon-close.svg` glyph (18×18, stroke 1.6) tinted
 * `text/secondary`, flex-centered in the frame.
 *
 * Reuses the existing dismiss/close output (no new output case — spec §6).
 */
export function CloseButton({
  onClick,
  ariaLabel = 'Close route',
  variant = 'tablet',
}: CloseButtonProps): ReactElement {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={`${styles.button} ${styles[variant]}`}
      data-testid="route-card-close"
      data-variant={variant}
      onClick={onClick}
    >
      <IconClose className={styles.icon} width="18" height="18" />
    </button>
  )
}
